/* eslint-disable @typescript-eslint/naming-convention */
import type { GeoRule } from '@ps/web-martech-cms-types';
import type { ContainerBlock } from '@ps/web-martech-cms-types/Main';
import classNames from 'clsx';
import React, { useMemo } from 'react';

import getBackgroundImageStyle from '@swf/core/utils/getBackgroundImageStyle';

import styles from '../../styles/styles.scss';
import CMSReferences from '../CMSReference/CMSReferences';

import useGeoVisibility from './hooks/useGeoVisibility';

interface Props {
  background_color?: string;
  font_color?: string;
}

const getDynamicBackgroundStyle = ({
  background_color: backgroundColor,
  font_color: fontColor
}: Props = {}) => {
  const styles: { background?: string; color?: string } = {};

  if (backgroundColor) {
    styles.background = backgroundColor;
  }

  if (fontColor) {
    styles.color = fontColor;
  }

  return styles;
};

export const ContainerVariantContext = React.createContext<{
  containerVariant?: ContainerBlock['container']['variant'];
}>({});

const CMSContainer: React.FC<React.PropsWithChildren<ContainerBlock>> = ({
  container: { variant, reference, visibility_rules, background_image, dynamic_background },
  children
}) => {
  const rules = useMemo(
    () =>
      visibility_rules.reduce((result, rule) => {
        const actualRule = rule as { geo_rule: GeoRule };
        return actualRule.geo_rule ? [...result, actualRule.geo_rule] : result;
      }, [] as GeoRule[]),
    [visibility_rules]
  );
  const [isVisible] = useGeoVisibility(rules);
  const isVideoPanel = reference[0]?._content_type_uid === 'video_panel';

  if (isVisible) {
    return (
      <div
        style={{
          ...getDynamicBackgroundStyle(dynamic_background),
          ...getBackgroundImageStyle(background_image?.url)
        }}
        className={classNames(
          {
            [styles.mainContainer]: !isVideoPanel,
            [styles.withBackgroundImage]: background_image?.url
          },
          styles[(variant as keyof typeof styles) ?? '']
        )}
      >
        <ContainerVariantContext.Provider value={{ containerVariant: variant }}>
          {reference.length ? <CMSReferences references={reference} /> : children}
        </ContainerVariantContext.Provider>
      </div>
    );
  }

  return null;
};

export default CMSContainer;
